import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC } from 'react'
import styled from 'styled-components'

import { makeColumns } from '@Components/Header/helpers'
import { CMSPosts } from '@Components/Header/MegaMenu/CMSPosts'
import { Link } from '@Components/Link'
import { NavigationEvent } from '@Events/Navigation'
import { Category } from '@Generated/graphql'
import { sectionInner } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

interface MegaMenuSingleCategoryProps {
  activeID?: number
  categories: Category[]
  menuID: number
}

interface SingleCategoryColumnProps {
  categories: Category[]
}

interface SingleCategoryColumnItemProps {
  category: Category
}

interface SingleCategoryItemProps {
  category: Category
  isParent?: boolean
  title?: string
}

interface WrapperProps {
  borderColor?: string
  color?: string
  subCategoryItemTextColor?: string
}

const SingleCategoryItem: FC<SingleCategoryItemProps> = (
  {
    category,
    isParent = false,
    title = '',
  }: SingleCategoryItemProps,
) => (
  <Link
    className={clsx({
      categoryItem: isParent,
      subCategoryItem: !isParent,
    })}
    event={new NavigationEvent({
      attributes: {
        dest_page_type: 'category',
        dest_page_type_id: category.id,
        source_id: 'mega_menu',
      },
      eventName: 'nav_link_click',
    })}
    href={category.url || ''}
  >
    {title || category.name}
  </Link>
)

const SingleCategoryColumnItem: FC<SingleCategoryColumnItemProps> = (
  {
    category,
  }: SingleCategoryColumnItemProps,
) => {
  const { t } = useTranslation()

  return (
    <div className="categoryItemsWrap">
      {/* Parent category */}
      <SingleCategoryItem
        category={category}
        isParent
      />

      {/* View all */}
      <SingleCategoryItem
        category={category}
        title={t('header:HEADER_MOBILE_MENU_VIEW_ALL_LABEL')}
      />

      {/* Show children */}
      {category.children && (
        map(category.children, item => (
          <SingleCategoryItem
            key={item.id}
            category={item}
          />
        ))
      )}
    </div>
  )
}

const SingleCategoryColumn: FC<SingleCategoryColumnProps> = (
  {
    categories,
  }: SingleCategoryColumnProps,
) => (
  <div className="categoryColumn">
    {map(categories, category => (
      <SingleCategoryColumnItem key={category.id} category={category} />
    ))}
  </div>
)

const Wrapper = styled.div<WrapperProps>`
  ${sectionInner};
  padding: 15px 35px;
  display: none;
  background-color: ${({ color, theme }) => color ?? theme.colors.white};
  /* TODO: Include in theme/colors system as greyLightest darkened 5% */
  border: 1px solid ${({ borderColor }) => borderColor ?? '#e8e8e8'};
  box-shadow: 0 10px 15px 0 rgba(0,0,0,0.05);

  &.isActive {
    display: flex;
    justify-content: space-between;
  }

  .leftSide {
    margin-right: 20px;
    padding-top: 10px;
  }

  .categoriesWrap {
    display: flex;
  }

  .categoryColumn {
    width: 200px;
    padding-right: 20px;
  }

  .categoryItemsWrap {
    margin-bottom: 20px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  .categoryItem {
    color: ${({ theme }) => theme.colors.greyDark};
    display: flex;
    align-items: center;
    padding-bottom: 4px;
    text-transform: uppercase;
    &:hover {
      text-decoration: underline;
    }
  }

  .subCategoryItem {
    display: block;
    font-size: 0.875rem;
    color: ${({ subCategoryItemTextColor, theme }) => subCategoryItemTextColor
      ?? theme.colors.grey};
    padding: 3.5px 0;
    &:hover {
      text-decoration: underline;
    }
  }
`

export const MegaMenuSingleCategory: FC<MegaMenuSingleCategoryProps> = (
  {
    activeID,
    categories,
    menuID,
  }: MegaMenuSingleCategoryProps,
) => {
  const columns = makeColumns(categories)

  return (
    <Wrapper
      className={clsx({
        isActive: activeID === menuID,
      })}
    >
      {/* Left side */}
      <div className="leftSide">
        {/* Categories */}
        <div className="categoriesWrap">
          {map(columns, ({ categories: columnCategories, id }) => (
            <SingleCategoryColumn
              key={id}
              categories={columnCategories}
            />
          ))}
        </div>
      </div>

      {/* Right side */}
      <CMSPosts />
    </Wrapper>
  )
}
